import { motion } from "framer-motion";
import { Timer, MessagesSquare, ListChecks, CalendarCheck2, UserRoundCheck, TrendingUp } from "lucide-react";

const metrics = [
  {
    icon: Timer,
    title: "Response Time",
    desc: "How quickly new inquiries receive a meaningful response.",
    example: "< 5 min",
  },
  {
    icon: MessagesSquare,
    title: "Contact Rate",
    desc: "How many leads actually enter a two-way conversation.",
    example: "68%",
  },
  {
    icon: ListChecks,
    title: "Follow-Up Completion",
    desc: "Whether planned follow-up occurs consistently.",
    example: "92%",
  },
  {
    icon: CalendarCheck2,
    title: "Appointments Booked",
    desc: "Buyer consultations, listing consultations and other agreed appointments generated through the workflow.",
    example: "11",
  },
  {
    icon: UserRoundCheck,
    title: "Leads Recovered",
    desc: "Previously missed or inactive prospects returned to active conversation.",
    example: "7",
  },
  {
    icon: TrendingUp,
    title: "Pipeline Opportunity Influenced",
    desc: "Estimated opportunity associated with leads touched by the workflow.",
    example: "$86,400",
  },
];

export default function ProofOfResults() {
  return (
    <section id="proof" data-testid="proof-section" className="py-20 md:py-28 bg-[#0B0F19]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            How We Prove <span className="text-gradient">the Results</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl text-slate-200 leading-relaxed">
            Every workflow should produce measurable evidence. We track the movement from inquiry
            to conversation to appointment so you can see what is improving and where leads are
            still getting stuck.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {metrics.map((m, i) => (
            <motion.div
              key={m.title}
              data-testid={`metric-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.07, duration: 0.5 }}
              className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 pt-7 overflow-hidden transition-[border-color] duration-300 hover:border-indigo-400/40"
            >
              <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-cyan-400/70 via-indigo-500/70 to-purple-500/70" />
              <div className="flex items-center gap-3 mb-3">
                <m.icon size={20} strokeWidth={1.75} className="text-cyan-300 shrink-0" />
                <h3 className="font-heading font-bold text-lg text-white">{m.title}</h3>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          data-testid="pipeline-influenced-note"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="mt-6 text-sm text-slate-400 leading-relaxed max-w-3xl"
        >
          "Pipeline Opportunity Influenced" reflects the estimated opportunity associated with
          leads the workflow touched. Influenced does not mean revenue solely attributable to
          iVIBES.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 rounded-2xl border border-dashed border-white/20 bg-white/[0.02] overflow-hidden"
          data-testid="proof-illustrative-example"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 bg-white/[0.03]">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-cyan-400/40" />
            <span className="ml-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Performance snapshot
            </span>
          </div>
          <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-5">
            <span className="rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-300 text-[11px] font-bold uppercase tracking-wider px-3 py-1">
              Illustrative Example
            </span>
            <p className="text-xs text-slate-500">
              Sample values for demonstration only — not client results.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {metrics.map((m) => (
              <div key={m.title} className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3.5">
                <p className="font-heading font-extrabold text-xl text-cyan-300 tracking-tight">
                  {m.example}
                </p>
                <p className="mt-1 text-[11px] text-slate-400 leading-snug">{m.title}</p>
              </div>
            ))}
          </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
